/**
 * Views API Service
 * 
 * Client-side API service for Views CRUD operations, following the existing
 * SHARP API service patterns and integrating with the Gateway REST API.
 * 
 * Since the ApiService class doesn't expose public HTTP methods, we'll create
 * a simplified service that can be extended when Views endpoints are added to the main ApiService.
 */

/// <reference path="../../types/axios-fix.d.ts" />
import axios, { AxiosInstance } from 'axios';
import { environment } from '../../environments/environment';
import { logError } from '../utils/error-handler';
import {
  View,
  CreateViewRequest,
  UpdateViewRequest,
  DuplicateViewRequest,
  RenderViewRequest,
  ViewRenderResponse,
  ViewFilter,
  ViewListResponse,
  ViewStatsResponse,
  ViewDeleteResponse,
  ViewApiResponse,
} from '../types/view.types';

export class ViewsApiService {
  private instance: AxiosInstance;
  private readonly baseUrl = ''; // Empty because environment.apiUrl already includes /api/v2

  constructor() {
    this.instance = axios.create({
      baseURL: environment.apiUrl,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    }) as any;

    // Add request interceptor for authentication
    this.instance.interceptors.request.use((config) => {
      const token = localStorage.getItem('accessToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Add response interceptor for error handling with enhanced error details
    this.instance.interceptors.response.use(
      (response) => response,
      (error) => {
        // Use safe error logging to prevent webpack overlay issues
        const errorMessage = error.response?.data?.error?.message || 
                            error.response?.data?.message || 
                            error.message || 
                            'An unexpected error occurred';
                            
        logError('[Views API Error]', {
          message: errorMessage,
          url: error.config?.url,
          method: error.config?.method?.toUpperCase(),
          status: error.response?.status,
          statusText: error.response?.statusText,
          errorData: error.response?.data,
          requestData: error.config?.data,
        });

        // Handle auth errors by redirecting to login
        if (error.response?.status === 401) {
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          window.location.href = '/login';
        }

        // Create enhanced error with more details
        const enhancedError = new Error(errorMessage);
        
        // Attach additional error details for better debugging
        (enhancedError as any).details = {
          status: error.response?.status,
          code: error.response?.data?.error?.code,
          serviceName: error.response?.data?.error?.serviceName,
          requestId: error.response?.data?.error?.requestId,
          originalError: error.response?.data,
          requestPayload: error.config?.data,
        };

        return Promise.reject(enhancedError);
      }
    );
  }

  /**
   * Extract data from API response following SHARP API Response Standard
   */
  private extractData<T>(response: any): T {
    // Handle direct data responses
    if (response.data && typeof response.data === 'object') {
      // If response.data has a 'data' property, extract it (SHARP API Response Standard)
      if ('data' in response.data) {
        return response.data.data as T;
      }
      // Otherwise return the data directly
      return response.data as T;
    }
    return response as T;
  }

  /**
   * Create a new view
   */
  async createView(viewData: CreateViewRequest): Promise<View> {
    // Log the request for debugging
    console.log('[Views API] Creating view with data:', {
      ...viewData,
      mdx_content: viewData.mdx_content ? `${viewData.mdx_content.substring(0, 100)}...` : undefined
    });
    
    const response = await this.instance.post<ViewApiResponse<View>>(
      `${this.baseUrl}/views`,
      viewData
    );
    return this.extractData<View>(response);
  }

  /**
   * Get view by ID
   * @param viewId - The ID of the view to fetch
   * @param teamId - The team ID for subscription validation (REQUIRED)
   */
  async getView(viewId: string, teamId: string): Promise<View> {
    const response = await this.instance.get<ViewApiResponse<View>>(
      `${this.baseUrl}/views/${viewId}?team_id=${encodeURIComponent(teamId)}`
    );
    return this.extractData<View>(response);
  }

  /**
   * Update existing view
   * @param viewId - The ID of the view to update
   * @param updates - The updates to apply
   * @param teamId - The team ID for subscription validation (REQUIRED)
   */
  async updateView(viewId: string, updates: UpdateViewRequest, teamId: string): Promise<View> {
    const response = await this.instance.put<ViewApiResponse<View>>(
      `${this.baseUrl}/views/${viewId}`,
      { ...updates, team_id: teamId }
    );
    return this.extractData<View>(response);
  }

  /**
   * Delete view
   * @param viewId - The ID of the view to delete
   * @param teamId - The team ID for subscription validation (REQUIRED)
   */
  async deleteView(viewId: string, teamId: string): Promise<ViewDeleteResponse> {
    const response = await this.instance.delete<ViewApiResponse<ViewDeleteResponse>>(
      `${this.baseUrl}/views/${viewId}?team_id=${encodeURIComponent(teamId)}`
    );
    return this.extractData<ViewDeleteResponse>(response);
  }

  /**
   * List views with filtering and pagination
   */
  async listViews(filters: ViewFilter = {}): Promise<ViewListResponse> {
    const params = new URLSearchParams();
    
    // Add filter parameters
    if (filters.organization_id) params.set('organization_id', filters.organization_id);
    if (filters.team_id) params.set('team_id', filters.team_id);
    if (filters.category) params.set('category', filters.category);
    if (filters.status !== undefined) params.set('status', filters.status.toString());
    if (filters.tagIds?.length) {
      filters.tagIds.forEach(tagId => params.append('tagIds', tagId));
    }
    if (filters.search) params.set('search', filters.search);
    if (filters.created_by) params.set('created_by', filters.created_by);
    if (filters.page) params.set('page', filters.page.toString());
    if (filters.limit) params.set('limit', filters.limit.toString());
    if (filters.sort_by) params.set('sort_by', filters.sort_by);
    if (filters.sort_order) params.set('sort_order', filters.sort_order);

    const response = await this.instance.get<ViewListResponse>(
      `${this.baseUrl}/views?${params.toString()}`
    );
    
    // Handle Gateway V2 response structure for list views
    // Response structure: { data: View[], metadata: { pagination: {...} } }
    if (response.data && typeof response.data === 'object' && 'data' in response.data) {
      return response.data as ViewListResponse;
    }
    
    return this.extractData<ViewListResponse>(response);
  }

  /**
   * Duplicate an existing view
   */
  async duplicateView(viewId: string, options: DuplicateViewRequest = {}): Promise<View> {
    const response = await this.instance.post<ViewApiResponse<View>>(
      `${this.baseUrl}/views/${viewId}/duplicate`,
      options
    );
    return this.extractData<View>(response);
  }

  /**
   * Render view with MDX compilation
   */
  async renderView(viewId: string, renderOptions: RenderViewRequest = {}): Promise<ViewRenderResponse> {
    const response = await this.instance.post<ViewApiResponse<ViewRenderResponse>>(
      `${this.baseUrl}/views/${viewId}/render`,
      renderOptions
    );
    return this.extractData<ViewRenderResponse>(response);
  }

  /**
   * Get view statistics
   */
  async getViewStats(organizationId?: string, teamId?: string): Promise<ViewStatsResponse> {
    const params = new URLSearchParams();
    if (organizationId) params.set('organization_id', organizationId);
    if (teamId) params.set('team_id', teamId);

    const response = await this.instance.get<ViewApiResponse<ViewStatsResponse>>(
      `${this.baseUrl}/views/stats?${params.toString()}`
    );
    
    // Handle Gateway V2 response structure
    if (response.data && typeof response.data === 'object' && 'data' in response.data) {
      return response.data.data as ViewStatsResponse;
    }
    
    return this.extractData<ViewStatsResponse>(response);
  }

  /**
   * Search views (convenience method using listViews)
   */
  async searchViews(
    query: string,
    filters: Omit<ViewFilter, 'search'> = {}
  ): Promise<ViewListResponse> {
    return this.listViews({ ...filters, search: query });
  }

  /**
   * Get views by category
   */
  async getViewsByCategory(
    category: string,
    filters: Omit<ViewFilter, 'category'> = {}
  ): Promise<ViewListResponse> {
    return this.listViews({ ...filters, category: category as any });
  }

  /**
   * Get public views
   */
  async getPublicViews(filters: Omit<ViewFilter, 'status'> = {}): Promise<ViewListResponse> {
    return this.listViews({ ...filters, status: 3 }); // PUBLIC status
  }

  /**
   * Get views by organization
   */
  async getOrganizationViews(
    organizationId: string,
    filters: Omit<ViewFilter, 'organization_id'> = {}
  ): Promise<ViewListResponse> {
    return this.listViews({ ...filters, organization_id: organizationId });
  }

  /**
   * Get views by team
   */
  async getTeamViews(
    teamId: string,
    filters: Omit<ViewFilter, 'team_id'> = {}
  ): Promise<ViewListResponse> {
    return this.listViews({ ...filters, team_id: teamId });
  }

  /**
   * Get views created by user
   */
  async getUserViews(
    userId: string,
    filters: Omit<ViewFilter, 'created_by'> = {}
  ): Promise<ViewListResponse> {
    return this.listViews({ ...filters, created_by: userId });
  }

  /**
   * Upload an asset for a view
   */
  async uploadViewAsset(file: File, viewId?: string): Promise<{ url: string; assetId: string }> {
    const formData = new FormData();
    formData.append('file', file);
    if (viewId) {
      formData.append('viewId', viewId);
    }

    const response = await this.instance.post<ViewApiResponse<{ url: string; assetId: string }>>(
      `${this.baseUrl}/views/assets/upload`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return this.extractData<{ url: string; assetId: string }>(response);
  }

  /**
   * Get the URL for a view asset
   */
  getAssetUrl(assetId: string): string {
    // Return the URL for accessing the asset
    return `${this.baseUrl}/views/assets/${assetId}`;
  }
}

// Export singleton instance following the existing pattern
export const viewsApiService = new ViewsApiService();